import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import useDefaultModal from './components/hooks/useDefaultModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import RequisitanteModal from './components/modalServices/RequisitanteModal';
import Toolbar from './components/Toolbar';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ResponsiveShadow from './components/ResponsiveShadow';

function Requisitante() {
  const [Requisitante, setRequisitante] = useState({
    cnpj: '',
    num: '',
    nome: '',
    responsavelNome: '',
    responsavelFuncao: '',
    responsavelMatricula: '',
  });
  const [Requisitantes, setRequisitantes] = useState([]);
  const [filtroNome, setFiltroNome] = useState('');
  const navigate = useNavigate();

  // Filtrar requisitantes por nome
  const requisitantesFiltrados = Requisitantes.filter(req =>
    req.nome.toLowerCase().includes(filtroNome.toLowerCase())
  );

  const totalItems = requisitantesFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const selectedRequisitantes = requisitantesFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );
  const token = localStorage.getItem('token');
  const location = useLocation();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  // Estados para o modal
  const [showRequisitanteModal, setShowRequisitanteModal] = useState(false);
  const [selectedRequisitante, setSelectedRequisitante] = useState(null);

  useEffect(() => {
    const storedCnpj = localStorage.getItem('cnpj');
    if (location.state && location.state.requisitante) {
      setRequisitante({
        ...location.state.requisitante,
        cnpj: storedCnpj || location.state.requisitante.cnpj,
      });
    } else {
      setRequisitante(prev => ({
        ...prev,
        cnpj: storedCnpj || '',
      }));
    }
  }, [location.state]);

  const fetchRequisitantes = () => {
    if (Requisitante.cnpj) {
      axios
        .get(`${API_BASE_URL.REQUISITANTE}/all_cnpj`, {
          params: { cnpj: Requisitante.cnpj },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(response => {
          const sortedData = [...response.data].sort((a, b) =>
            a.nome.localeCompare(b.nome)
          );
          setRequisitantes(sortedData);
          setIsAscending(true);
        })
        .catch(error => {
          console.error('Erro ao buscar requisitantes:', error);
        });
    }
  };

  useEffect(() => {
    fetchRequisitantes();
  }, [Requisitante.cnpj]);

  function handleRowClick(requisitante) {
    // Se clicar no mesmo requisitante já selecionado, desmarca
    if (Requisitante.num === requisitante.num) {
      setRequisitante({
        cnpj: requisitante.cnpj,
        num: '',
        nome: '',
        responsavelNome: '',
        responsavelFuncao: '',
        responsavelMatricula: '',
      });
      setSelectedRequisitante(null);
    } else {
      setRequisitante({
        cnpj: requisitante.cnpj,
        num: requisitante.num,
        nome: requisitante.nome,
        responsavelNome: requisitante.responsavelNome || '',
        responsavelFuncao: requisitante.responsavelFuncao || '',
        responsavelMatricula: requisitante.responsavelMatricula || '',
      });
      setSelectedRequisitante(requisitante);
    }
  }

  function handleNewClick() {
    setRequisitante({
      cnpj: Requisitante.cnpj,
      num: '',
      nome: '',
      responsavelNome: '',
      responsavelFuncao: '',
      responsavelMatricula: '',
    });
  }

  // Funções para controlar o modal
  function handleOpenModal(requisitante = null) {
    setSelectedRequisitante(requisitante);
    setShowRequisitanteModal(true);
  }

  function handleCloseModal() {
    setShowRequisitanteModal(false);
    setSelectedRequisitante(null);
  }

  function handleModalSuccess(data, isEditing) {
    fetchRequisitantes();
    handleCloseModal();
    showDefaultModal(
      'success',
      isEditing
        ? 'Requisitante atualizado com sucesso!'
        : 'Requisitante criado com sucesso!'
    );
  }

  function handleDeleteClick(requisitanteToDelete) {
    showConfirmModal('delete', () => handleConfirmDelete(requisitanteToDelete));
  }

  function handleConfirmDelete(requisitanteToDelete) {
    axios
      .delete(`${API_BASE_URL.REQUISITANTE}/delete`, {
        params: {
          cnpj: requisitanteToDelete.cnpj,
          num: requisitanteToDelete.num,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        fetchRequisitantes();
        handleNewClick();
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir requisitante:', error);
        showDefaultModal('error', 'Erro ao excluir o registro!');
      });
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Requisitantes Cadastrados" icon="bi-people-fill" closeRoute="/home" backButton={false} />
        <PageContent style={{ paddingBottom: 0 }}>
          <Toolbar
            selectedId={selectedRequisitante ? selectedRequisitante.num : null}
            onCreate={() => handleOpenModal()}
            onEdit={(id) => { if (id) handleOpenModal(Requisitantes.find(r => r.num === id)); }}
            onDelete={(id) => { if (id) handleDeleteClick(Requisitantes.find(r => r.num === id)); }}
            onSearch={(v) => setFiltroNome((v || '').toUpperCase())}
            searchValue={filtroNome}
            searchPlaceholder="Digite o nome do requisitante..."
            searchWidth="250px"
          />

          <TableContainer tableClass="table-custom-radius">
        <TableHeader columns={[
          { label: <><i className="bi bi-person me-1"></i>Nome</> },
          { label: <><i className="bi bi-person me-1"></i>Responsável</> },
          { label: <><i className="bi bi-card-text me-1"></i>CPF</> },
          { label: <><i className="bi bi-card-text me-1"></i>Matrícula</> },
          { label: <><i className="bi bi-briefcase me-1"></i>Função</> },
        ]} />
        <tbody>
          {selectedRequisitantes.length > 0 ? (
            selectedRequisitantes.map((req, index) => (
              <tr
                key={index}
                onClick={() => handleRowClick(req)}
                style={{ cursor: 'pointer' }}
                className={Requisitante.num === req.num ? 'table-primary' : ''}
                title={
                  Requisitante.num === req.num
                    ? 'Clique novamente para desmarcar'
                    : 'Clique para selecionar'
                }
              >
                <td className="fw-medium">{req.nome}</td>
                <td>{req.responsavelNome || '-'}</td>
                <td>{req.responsavelCpf || '-'}</td>
                <td>{req.responsavelMatricula || '-'}</td>
                <td>{req.responsavelFuncao || '-'}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className="text-center text-muted py-4">
                <i className="bi bi-inbox display-6 d-block mb-2"></i>
                {filtroNome
                  ? `Nenhum requisitante encontrado para "${filtroNome}"`
                  : 'Nenhum requisitante cadastrado'}
              </td>
            </tr>
          )}
        </tbody>
      </TableContainer>
      <PageFooter>
        <Pagination10 {...setPagination} />
      </PageFooter>
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
      <RequisitanteModal
        isOpen={showRequisitanteModal}
        requisitante={selectedRequisitante}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
      />
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}

export default Requisitante;
